import type {PaginationState} from "@tanstack/react-table";
import type {ServerTableAllRows} from "./types";

interface CollectOptions<TData> {
    pageSize: number
    /** Counted in printed rows: a row plus everything nested under it. */
    maxRows: number
    getSubRows?: (row: TData, index: number) => TData[] | undefined
    /** List rows to skip before collecting; the first request starts on the page that holds the first wanted row. */
    offset?: number
    /** At most this many list rows from `offset`; reaching it is not truncation. */
    limit?: number
}

/**
 * Pages through a server query until every row is collected or the cap is reached. The cap counts
 * printed rows, not server rows: a grouped table prints each group and every row inside it, so a
 * group weighs as much as all of them. Groups are kept whole - the first one always, even when it
 * alone exceeds the cap. `offset` and `limit` narrow the collection to a slice of the list, such as
 * one table page; `truncated` then says only whether the cap cut that slice short.
 */
export const collectServerTableRows = async <TData>(
    fetchPage: (pagination: PaginationState) => Promise<{content: TData[], totalElements: number}>,
    {pageSize, maxRows, getSubRows, offset = 0, limit}: CollectOptions<TData>
): Promise<ServerTableAllRows<TData>> => {
    const printedRowsOf = (row: TData, index: number): number => 1 + (getSubRows?.(row, index) ?? [])
        .reduce((sum, child, childIndex) => sum + printedRowsOf(child, childIndex), 0);
    const wantedOf = (total: number) => Math.min(limit ?? Infinity, Math.max(total - offset, 0));
    const rows: TData[] = [];
    let printedRows = 0;
    let total = 0;
    let truncated = false;
    let skip = offset % pageSize;

    for (let pageIndex = Math.floor(offset / pageSize); !truncated; pageIndex++) {
        const page = await fetchPage({pageIndex, pageSize});
        const content = (page.content ?? []).slice(skip);
        skip = 0;
        total = page.totalElements ?? offset + rows.length + content.length;
        const wanted = wantedOf(total);

        for (let index = 0; index < content.length && rows.length < wanted; index++) {
            const weight = printedRowsOf(content[index], index);
            if (rows.length > 0 && printedRows + weight > maxRows) {
                truncated = true;
                break;
            }
            rows.push(content[index]);
            printedRows += weight;
        }

        if (!content.length || rows.length >= wanted) break;
        if (printedRows >= maxRows) truncated = true;
    }

    return {
        rows,
        printedRows,
        total: Math.max(total, rows.length),
        truncated: truncated || wantedOf(total) > rows.length
    };
};
